<template>
  <div>
    <div v-if="loading" class="text-center pa-5">
      <v-progress-circular width="2" color="primary" indeterminate />
    </div>
    <v-card v-else flat>
      <v-card-text>
        <form class="form_datos_personales">
          <v-layout wrap>
            <v-flex xs12 sm6 lg6>
              <v-text-field
                v-model="paciente.nombre"
                data-test="patient-edit-input-name"
                filled
                dense
                outlined
                type="text"
                class="mx-1"
                :label="$t('patient.first_name') + ' *'"
                :rules="ruleRequired"
                @change="modificaDato(true)"
              />
            </v-flex>
            <v-flex xs12 sm6 lg6>
              <v-text-field
                v-model="paciente.apellidos"
                data-test="patient-edit-input-surname"
                filled
                dense
                outlined
                type="text"
                class="mx-1"
                :label="$t('patient.last_name') + ' *'"
                :rules="ruleRequired"
                @change="modificaDato(true)"
              />
            </v-flex>
            <v-flex xs12 sm8 lg8>
              <v-identify-document
                data-test="patient-input-document-type"
                :dense="true"
                :filled="true"
                :id-document="paciente.dni"
                :id-document-type="paciente.tipoDocumento"
                class="mx-1"
                use-required
                @valid="handleIdErrors"
                @input="setIdDocument"
              />
            </v-flex>
            <v-flex xs12 sm4>
              <v-menu
                ref="fechaNacimientoMenu"
                v-model="fechaNacimientoMenu"
                :close-on-content-click="false"
                :return-value.sync="paciente.fechaNacimiento"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="dateFormatted"
                    filled
                    dense
                    outlined
                    :label="$t('patient.birthdate') + ' *'"
                    append-icon="mdi-calendar"
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  ref="picker"
                  v-model="date"
                  data-test="patient-edit-input-birthdate"
                  no-title
                  scrollable
                  class="input_fnacimiento"
                  first-day-of-week="1"
                  :locale="this.$i18n.locale"
                  :min="datePickerMinDate"
                  :max="new Date().toISOString().substr(0, 10)"
                  @input="$refs.fechaNacimientoMenu.save(date)"
                />
              </v-menu>
            </v-flex>
            <v-flex v-if="!es_tutelado" sm6 lg6>
              <v-text-field
                v-model="paciente.email"
                data-test="patient-edit-input-email"
                filled
                dense
                outlined
                type="mail"
                class="mx-1"
                :label="$t('message.email') + ' *'"
                :rules="ruleEmail"
                @change="modificaDato(true)"
              />
            </v-flex>
            <v-flex v-if="!es_tutelado" sm6 lg6>
              <v-text-field
                v-model="paciente.email_repeat"
                data-test="patient-edit-input-email-repeat"
                filled
                dense
                outlined
                onpaste="return false"
                class="mx-1"
                :label="$t('message.email_repeat') + ' *'"
                type="mail"
                required
                @change="modificaDato(true)"
              />
            </v-flex>
            <v-flex v-if="!es_tutelado" xs12 sm4 lg4 class="px-1">
              <v-tel-field
                v-model="patientPhone"
                data-test="patient-edit-input-phone"
                outlined
                filled
                dense
                :label="$t('common.phone_number') + (isPhoneMandatoryForPatient ? ' *' : '')"
                :allow-web-phone="true"
                :rules="isPhoneMandatoryForPatient ? ruleRequired : []"
                v-bind="bindProps"
                @input="onInputPhone"
                @call="callEvent"
              />
            </v-flex>
            <v-flex xs12 sm4 lg4>
              <VGenderSelect
                :filled="false"
                :dense="true"
                :rounded="false"
                :gender="paciente.sexo"
                @input="inputGender"
              />
            </v-flex>

            <v-flex xs12 sm4 lg4>
              <v-select
                v-model="paciente.idioma"
                data-test="patient-edit-input-lang"
                filled
                dense
                outlined
                class="mx-1"
                :items="idiomas"
                :label="$t('common.lang')"
                auto
                required
                item-text="name"
                item-value="id"
              />
            </v-flex>
            <template v-if="!es_tutelado">
              <v-flex v-if="!es_tutelado" xs12 sm4 lg4>
                <v-autocomplete
                  v-model="paciente.pais_iso"
                  data-test="patient-edit-input-country"
                  filled
                  dense
                  outlined
                  class="mx-1"
                  :items="paises"
                  item-text="name"
                  item-value="abbr"
                  hide-no-data
                  :label="$t('patient.pais')"
                  @input="modificaDato(true)"
                />
              </v-flex>

              <v-flex v-if="!es_tutelado" xs12 sm4 lg4>
                <v-select
                  v-if="paciente.pais === 'España'"
                  v-model="paciente.provincia_id"
                  filled
                  dense
                  outlined
                  class="mx-1"
                  :items="provincias"
                  :label="$t('common.province')"
                  auto
                  item-text="nombre"
                  item-value="id"
                />
                <v-text-field
                  v-else
                  v-model="paciente.provincia"
                  data-test="patient-edit-input-country"
                  filled
                  dense
                  outlined
                  class="mx-1"
                  :label="$t('common.province')"
                  type="text"
                  @change="modificaDato(true)"
                />
              </v-flex>
              <v-flex v-if="!es_tutelado" xs12 sm4 lg4>
                <v-text-field
                  v-model="paciente.localidad"
                  data-test="patient-edit-input-location"
                  filled
                  dense
                  outlined
                  class="mx-1"
                  :label="$t('common.location')"
                  type="text"
                  @change="modificaDato(true)"
                />
              </v-flex>

              <v-flex v-if="!es_tutelado" sm3 lg3>
                <v-text-field
                  v-model="paciente.codigoPostal"
                  data-test="patient-edit-input-cp"
                  filled
                  dense
                  outlined
                  class="mx-1"
                  :label="$t('common.cp')"
                  type="text"
                  @change="modificaDato(true)"
                />
              </v-flex>
              <v-flex v-if="!es_tutelado" sm9 lg9>
                <v-text-field
                  v-model="paciente.direccion"
                  data-test="patient-edit-input-address"
                  filled
                  dense
                  outlined
                  class="mx-1"
                  :label="$t('common.address')"
                  type="text"
                  append-icon="mdi-map-marker"
                  @change="modificaDato(true)"
                />
              </v-flex>
            </template>

            <v-flex xs12>
              <v-textarea
                v-model="paciente.notas"
                filled
                outlined
                :label="$t('patient.notas')"
                @change="modificaDato(true)"
              />
            </v-flex>
            <v-flex xs12 sm3 lg3>
              <v-text-field
                v-if="historialId > 0"
                v-model="paciente.num_historia"
                filled
                outlined
                class="mx-1"
                :label="$t('patient.num_historia')"
                type="text"
                @change="modificaDato(true)"
              />
            </v-flex>
            <v-flex v-if="showHealthCardNumber" xs12 sm12 lg12>
              <v-text-field
                v-model="paciente.numsegsocial"
                filled
                dense
                outlined
                class="mx-1"
                :label="$t('common.numero_seg_social')"
                type="text"
                required
              />
            </v-flex>
            <v-flex xs12 class="text-right">
              <v-btn
                v-if="!es_tutelado && !loading"
                class="ma-2"
                :disabled="disabledButton"
                color="primary"
                outlined
                @click="cuidarAlguien(true)"
              >
                {{ $t('patient.cuidar_alguien') }}
              </v-btn>
              <v-btn data-test="patient-edit-button-save" depressed :disabled="disabledButton" color="primary" @click="editarPaciente()">
                {{ $t('common.save') }}
              </v-btn>
            </v-flex>
            <v-flex>
              <Cuidar
                :paciente="paciente"
                :es_tutelado="es_tutelado"
                :mostrar_nuevo="dialog_nuevo_cuidar"
                @dialogNuevoCuidar="cuidarAlguien"
                @mostrarAlert="mostrarAlert"
              />
            </v-flex>
          </v-layout>
        </form>
      </v-card-text>
    </v-card>
    <v-snackbar
      v-model="snackbar"
      :timeout="timeout"
      :color="alerta.color"
      top="top"
      :multi-line="mode === 'multi-line'"
      :vertical="mode === 'vertical'"
    >
      <v-icon v-if="alerta.icono !== ''">{{ alerta.icono }} </v-icon>&nbsp;{{ alerta.texto }}
    </v-snackbar>
    <!-- Dialog Salir sin guardar -->
    <div class="text-center">
      <v-dialog v-model="dialogSalirSinGuardar" width="500">
        <v-card>
          <v-card-title class="headline lighten-2">{{ $t('common.titulo_salir_sin_guardar') }} </v-card-title>
          <v-divider />
          <v-card-text>{{ $t('common.salir_sin_guardar') }}</v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" dark text @click="dialogSalirSinGuardar = false"
            >{{ $t('common.cancel') }}
            </v-btn>
            <v-btn color="primary" dark text @click="confirmarSalir">{{ $t('common.confirm') }} </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import VIdentifyDocument from '@/modules/VuetifyIdentityDocument/components/VIdentifyDocument.vue';
import Cuidar from "@/components/ehealth/paciente/Cuidar.vue";
import {defaultCountry, editPatientPersonalDataUrl, getHeader, getPacienteUrl} from "@/config/config";
import {mapState} from "vuex";
import {isPhoneMandatoryForPatient, setSelectorsDocumentType, setSelectorsLanguages} from "@/utils";
import {datePickerMinDate} from "@/services/dateHelper";
import VGenderSelect from "@/modules/VuetifyGender/components/VGenderSelect.vue";
import {isValidDni} from "nif-dni-nie-cif-validation";
import {spainProvinces} from "@/utils/spainProvinces";
import {default as countries } from "@/api/country";

export default {
  name: 'PersonalData',
  components: {
    VGenderSelect,
    Cuidar,
    VIdentifyDocument
  },
  data() {
    return {
      loading: true,
      esta_modificando: false,
      disabledButton: true,
      snackbar: false,
      timeout: 5000,
      mode: '',
      alerta: {
        texto: '',
        icono: '',
        color: 'info',
      },
      dialogSalirSinGuardar: false,
      pageNext: {},
      authUser: JSON.parse(window.localStorage.getItem('auth_ehealth')),
      paciente: null,
      dniError: false,
      isSavedDocumentIdValid: false,
      isPhoneMandatoryForPatient: isPhoneMandatoryForPatient(),
      patientPhone: '',
      phonePatient: {
        isValid: false,
      },
      isValidPhone: true,
      dialog_nuevo_cuidar: false,
      date: null,
      dateFormatted: null,
      fechaNacimientoMenu: false,
      datePickerMinDate: datePickerMinDate(),
      es_tutelado: false,
      rules: {},
      ruleRequired: [],
      ruleEmail: [],
      bindProps: {
        mode: 'international',
        defaultCountry: defaultCountry ? defaultCountry : 'ES',
        preferredCountries: ['ES'],
        disabledFetchingCountry: false,
        disabled: false,
        disabledFormatting: false,
        required: false,
        enabledCountryCode: false,
        enabledFlags: true,
        onlyCountries: [],
        ignoredCountries: [],
        autocomplete: 'off',
        name: 'telephonePatient',
        maxLen: 17,
        wrapperClasses: '',
        inputClasses: '',
        dropdownOptions: {
          disabledDialCode: false,
        },
        inputOptions: {
          showDialCode: false,
        },
      },
      idiomas: [
        { id: 'es', name: 'Español' },
        { id: 'en', name: 'Ingles' },
      ],
      paises: countries,
      provincias: spainProvinces(),
      historialId: null,
      showHealthCardNumber: false,

      tiposDocumento: [
        { id: 1, name: 'DNI' },
        { id: 2, name: 'NIE' },
        { id: 3, name: 'Pasaporte' },
        { id: 4, name: 'Otro' },
      ],
      tipoDocumento: 0,

      isProfessional:
        JSON.parse(window.localStorage.getItem('auth_ehealth')) &&
        JSON.parse(window.localStorage.getItem('auth_ehealth'))['rol'] === 2,
    };
  },

  computed: {
    ...mapState({
      permissions: state => state.app.permissions,
      appDisplayName: state => state.app.displayName,
      companyPermissions: state => state.account.authUser.company.permissions,
    }),
  },

  watch: {
    date() {
      this.dateFormatted = this.formatDate(this.date);
    },
    fechaNacimientoMenu(val) {
      val &&
      this.$nextTick(() => {
        this.$refs.picker.activePicker = 'YEAR';
        return;
      });
    },
    tipoDocumento() {
      this.verificarDocumentoIdentidad();
    },
  },

  mounted() {
    this.personalizacionesEntorno();

    if (typeof this.permissions.profilesFormWithInsuredNumber !== 'undefined') {
      this.showHealthCardNumber = this.permissions.profilesFormWithInsuredNumber;
    }

    this.setRules();
    this.getPatient();
  },

  methods: {
    mostrarAlert(alerta) {
      this.alerta = alerta;
      this.snackbar = true;
    },

    modificaDato(val) {
      this.esta_modificando = val;
    },

    confirmarSalir() {
      this.dialogSalirSinGuardar = false;
      this.pageNext();
    },

    setRules() {
      const regex = /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
      this.rules = {
        required: value => !!value || this.$t('common.obligatorio'),
        email: v => regex.test(v) || this.$t('errors.email_invalid'),
      };
      this.ruleEmail = [this.rules.required, this.rules.email];
      this.ruleRequired = [this.rules.required];
    },


    getPatient() {
      const param = {
        id: this.authUser.id,
        patient_id: this.$route.params.id,
        idioma: this.$i18n.locale,
        tab: 0,
        tabkey: "personal"
      };
      this.$http
        .post(getPacienteUrl, param, { headers: getHeader() })
        .then(response => {
          this.loading = true;
          if (response.status === 200 && response.data && response.data.d_personales) {
            this.paciente = response.data.d_personales;

            this.setPatientPhone();

            const splits = this.paciente.telefono !== null ? this.paciente.telefono.split(' ') : [];
            if (splits.length > 1) {
              this.paciente.telefono = splits[1];
            }

            this.phonePatient.isValid = true;
            this.paciente.phoneNumber = this.paciente.telefono;
            this.paciente.phonePrefix = this.paciente.codigoTelefono;
            if (this.paciente.tutor_id > 0) {
              this.es_tutelado = true;
            }
            this.date = this.paciente.fechaNacimiento;
            this.paciente.email_repeat = this.paciente.email;
            this.paciente.numsegsocial =
              typeof this.paciente.num_seg_social !== 'undefined' ? this.paciente.num_seg_social : '';
            this.tipoDocumento = response.data.d_personales.tipoDocumento;
            this.isSavedDocumentIdValid = isValidDni(response.data.d_personales.dni);
            this.paciente.dni_previo = this.paciente.dni;
            if (this.isProfessional) {
              this.historial = response.data.historial;
              this.paciente.num_historia = response.data.num_historia;
              this.paciente.notas = response.data.historial.notas;
            }
            // //get Informed Consents
            // if (this.authUser.show_informed_consent) {
            //   this.getInformedConsents();
            // }
            this.loading = false;
            this.disabledButton = false;
          } else {  
            this.alerta.texto = this.$t('errors.not_access_patient');
            this.alerta.color = 'error';
            this.snackbar = true;
            this.$router.push({ path: '/pacientes' });
          }
        })
        .catch(err => {
          console.log("ERROR TEST");
          console.log(err);
          this.$log.error(err);
          if (err.status === 403) {
            location.reload();
          }
        });
    },

    async editarPaciente() {
      this.loading = true;
      this.paciente.fechaNacimiento = this.date;
      this.alerta.color = 'error';
      this.alerta.icono = '';
      const es_valido = this.validarFormulario();
      if (es_valido) {
        this.disabledButton = true;

        try {
          const response = await this.$http.post(
            editPatientPersonalDataUrl,
            { id: this.authUser.id, patient: this.paciente, language: this.$i18n.locale },
            { headers: getHeader() }
          );

          if (response.status === 200 && response.data) {
            if (response.data === '1') {
              this.isSavedDocumentIdValid = true;
              this.alerta.texto = this.$t('success.edit_patient');
              this.alerta.color = 'success';
              this.alerta.icono = 'mdi-check';
            } else if (response.data === '2') {
              this.alerta.texto = this.$t('errors.form_invalid');
            } else if (response.data === '3') {
              this.alerta.texto = this.$t('errors.existing_patient', {
                name: this.appDisplayName,
              });
            } else {
              this.alerta.texto = this.$t('errors.edit_patient');
            }
          } else {
            this.alerta.texto = this.$t('errors.edit_patient');
          }
        } catch (e) {
          this.alerta.color = 'error';
          if (e.data && e.data.error) {
            this.alerta.texto = this.$t(e.data.error);
          } else {
            this.alerta.texto = this.$t('errors.try_again');
          }
        } finally {
          this.loading = false;
          this.snackbar = true;
          this.esta_modificando = false;
          this.disabledButton = false;
        }
      } else {
        this.snackbar = true;
      }
    },

    handleIdErrors(isValid) {
      this.$nextTick(() => {
        this.dniError = !isValid;
      });
    },

    validarFormulario() {
      this.alerta.texto = '';
      this.snackbar = false;
      const regex = /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;

      if (this.dniError) {
        this.alerta.texto = this.$t('patient.dni_invalid');
      }

      if (this.paciente.sexo === '') {
        this.alerta.texto = this.$t('errors.gender_required');
      }
      if (this.paciente.nombre.length === 0 || this.paciente.apellidos.length === 0) {
        this.alerta.texto = this.$t('patient.full_name_required');
      }
      if (this.paciente.nombre.length > 50) {
        this.alerta.texto = this.$t('patient.too_long_name');
      }
      if (this.paciente.apellidos.length > 50) {
        this.alerta.texto = this.$t('patient.too_long_surname');
      }
        // else if (this.paciente.dni.length < 1) {
        //   this.alerta.texto = this.$t('patient.dni_required');
        // }
        // else if (this.paciente.tipoDocumento === '0') {
        //   this.alerta.texto = this.$t('patient.type_dni_required');
      // }
      else if (this.paciente.fechaNacimiento) {
        // verificar que la fecha de nacimiento es menor a la actual
        const x = new Date();
        const fecha = this.paciente.fechaNacimiento.split('-');
        x.setFullYear(fecha[0], fecha[1] - 1, fecha[2]);
        const today = new Date();
        if (x >= today) {
          this.alerta.texto = this.$t('patient.birthdate_invalid');
        }
      }

      // validar email
      if (this.paciente.email === '') {
        this.alerta.texto = this.$t('errors.email_required');
      } else if (!regex.test(this.paciente.email)) {
        this.alerta.texto = this.$t('errors.email_invalid');
      } else if (this.paciente.email !== this.paciente.email_repeat) {
        this.alerta.texto = this.$t('errors.email_repeat');
      } else if (this.isPhoneMandatoryForPatient && typeof this.paciente.telefono === 'undefined') {
        this.alerta.texto = this.$t('registro.alert_phone_required');
        this.paciente.telefono = '';
      } else if (this.isPhoneMandatoryForPatient && (!this.phonePatient.isValid || !this.isValidPhone)) {
        this.alerta.texto = this.$t('errors.format_phone');
      } else if (!this.isPhoneMandatoryForPatient && typeof this.paciente.telefono === 'undefined') {
        this.paciente.telefono = '';
        this.paciente.phonePrefix = '';
      }

      return this.alerta.texto === '';
    },

    setIdDocument(data) {
      this.paciente.dni = data.idDocument;
      this.paciente.tipoDocumento = data.idDocumentType;
    },

    cuidarAlguien(estado) {
      this.dialog_nuevo_cuidar = estado;
    },

    inputGender(value) {
      this.paciente.sexo = value;
    },

    verificarDocumentoIdentidad() {
      this.paciente.tipoDocumento = this.tipoDocumento;
      if ((this.paciente.tipoDocumento === 1 || this.paciente.tipoDocumento === 2) && this.paciente.dni.length > 1) {
        let dni = this.paciente.dni;
        dni = dni.replace('-', '');
        const expresion_regular =
          this.paciente.tipoDocumento === 1
            ? /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/i
            : /^[XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
        if (expresion_regular.test(dni) !== true) {
          this.paciente.dni_previo = this.paciente.dni;
          this.paciente.dni = '';
          this.alerta.texto = this.$t('patient.dni_invalid');
        } else if (this.paciente.tipoDocumento === 1) {
          let numero = dni.substr(0, dni.length - 1);
          const letr = dni.substr(dni.length - 1, 1);
          numero = numero % 23;
          let letra = 'TRWAGMYFPDXBNJZSQVHLCKET';
          letra = letra.substring(numero, numero + 1);
          if (letra !== letr.toUpperCase()) {
            this.paciente.dni_previo = this.paciente.dni;
            this.paciente.dni = '';
            this.alerta.texto = this.$t('patient.dni_invalid_letra');
          }
        }
        if (this.paciente.dni === '') {
          this.alerta.icono = '';
          this.alerta.color = 'error';
          this.snackbar = true;
        }
      }
    },

    onInputPhone(formattedNumber, phone) {
      let prefixCountry = '+' + phone.country.dialCode;
      if (phone.valid && phone.country.iso2 !== phone.regionCode) {
        prefixCountry = phone.number.international.split(' ')[0];
      }
      this.paciente.telefono = phone.number.significant;
      this.paciente.codigoTelefono = prefixCountry;
      this.paciente.phonePrefix = prefixCountry;
      this.isValidPhone = phone.valid;
    },


    callEvent(number) {
      this.$dialog.call({
        number: number,
        name: this.paciente.fullName,
      });
    },

    /// TODO

    setUserLanguage() {
      if (this.idiomas.length === 1) {
        this.paciente.idioma = this.idiomas[0]['id'];
      }
    },

    setPatientPhone() {
      let phone = '';
      if (!this.isPhoneMandatoryForPatient && this.paciente.telefono === null) {
        this.patientPhone = this.paciente.phonePrefix = '';
        return;
      }

      if (this.paciente.telefono === null) {
        phone = '+34';
      } else {
        if (this.paciente.telefono.indexOf('+') === -1) {
          phone = this.paciente.codigoTelefono;
        }
        phone += this.paciente.telefono;
      }
      this.patientPhone = phone;
    },

    personalizacionesEntorno() {
      if (this.authUser !== null && this.authUser.nombreEmpresa === 'Docline Hospital') {
        this.idiomas.push({ id: 'ca', name: this.$t('common.catalan') });
      } else {
        this.idiomas[0]['name'] = this.$t('common.spanish');
        this.idiomas[1]['name'] = this.$t('common.english');
        this.tiposDocumento[2]['name'] = this.$t('patient.pasaporte');
        this.tiposDocumento[3]['name'] = this.$t('common.otro');
      }
      this.tiposDocumento = setSelectorsDocumentType(this.tiposDocumento);
      this.idiomas = setSelectorsLanguages(this.idiomas);
      this.setUserLanguage();
    },

    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    },

    parseDate(date) {
      if (!date) return null;
      const [day, month, year] = date.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.esta_modificando && to['name'] !== 'Login') {
      this.dialogSalirSinGuardar = true;
      this.pageNext = next;
      next(false);
    } else {
      next();
    }
  },
};
</script>
<style scoped lang="css">
.form_datos_personales,
.input_fnacimiento {
  color: #000 !important;
}
</style>
